import { useState, useEffect } from 'react';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Grid,
    Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { TableVirtuoso } from 'react-virtuoso';
import { storage } from '@goldblack/client-storage';
import { ContainerScroll } from '@goldblack/client-comps';

function parseCSV(text) {
    const lines = text.trim().split('\n');
    const result = [];
    for (let i = 1; i < lines.length; i++) {
        // skip header
        const match = lines[i].match(/"([^"]*)","([^"]*)","([^"]*)"/);
        if (match) {
            result.push({
                name: match[1],
                license: match[2],
                repository: match[3],
            });
        }
    }
    return result;
}

// 라이선스 URL 매핑
function getLicenseUrl(license) {
    const licenseMap = {
        MIT: 'https://opensource.org/licenses/MIT',
        ISC: 'https://opensource.org/licenses/ISC',
        'Apache-2.0': 'https://opensource.org/licenses/Apache-2.0',
        'BSD-2-Clause': 'https://opensource.org/licenses/BSD-2-Clause',
        'BSD-3-Clause': 'https://opensource.org/licenses/BSD-3-Clause',
        'GPL-2.0': 'https://opensource.org/licenses/GPL-2.0',
        'GPL-3.0': 'https://opensource.org/licenses/GPL-3.0',
        'LGPL-2.1': 'https://opensource.org/licenses/LGPL-2.1',
        'LGPL-3.0': 'https://opensource.org/licenses/LGPL-3.0',
        'MPL-2.0': 'https://opensource.org/licenses/MPL-2.0',
        Unlicense: 'https://opensource.org/licenses/unlicense',
        'CC0-1.0': 'https://creativecommons.org/publicdomain/zero/1.0/',
        '0BSD': 'https://opensource.org/licenses/0BSD',
        'Python-2.0': 'https://opensource.org/licenses/Python-2.0',
        'OpenRAIL++': 'https://huggingface.co/stabilityai/stable-diffusion-xl-base-1.0/blob/main/LICENSE.md',
    };

    // 정확한 매칭 먼저 시도
    if (licenseMap[license]) return licenseMap[license];

    // 부분 매칭 시도 (예: "MIT*" -> MIT)
    const normalized = license.replace(/[*\s]/g, '');
    for (const [key, url] of Object.entries(licenseMap)) {
        if (normalized.toUpperCase().includes(key.toUpperCase().replace(/-/g, ''))) {
            return url;
        }
    }

    // 매칭 안되면 SPDX 검색 페이지로
    return `https://spdx.org/licenses/${encodeURIComponent(license)}.html`;
}

// 공통 테이블 컴포넌트
function LicenseTable({ title, data, scrollParent, isLast }) {
    if (!data || data.length === 0) return null;

    return (
        <Box sx={{ my: 2 }}>
            <Typography variant='m_overline' color='primary.main'>
                {title}
            </Typography>
            <Table
                size='small'
                sx={(theme) => ({
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': {
                        borderColor: alpha(theme.palette.secondary.dark, 0.25),
                    },
                })}>
                <colgroup>
                    <col style={{ width: '45%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '40%' }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Package</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>License</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Repository</TableCell>
                    </TableRow>
                </TableHead>
            </Table>
            {scrollParent && (
                <TableVirtuoso
                    customScrollParent={scrollParent}
                    data={data}
                    overscan={200}
                    style={{ marginBottom: isLast ? 0 : 24 }}
                    components={{
                        Table: (props) => (
                            <Table
                                {...props}
                                size='small'
                                sx={(theme) => ({
                                    tableLayout: 'fixed',
                                    '& .MuiTableCell-root': {
                                        borderColor: alpha(theme.palette.secondary.dark, 0.25),
                                    },
                                })}>
                                <colgroup>
                                    <col style={{ width: '45%' }} />
                                    <col style={{ width: '15%' }} />
                                    <col style={{ width: '40%' }} />
                                </colgroup>
                                {props.children}
                            </Table>
                        ),
                        TableHead: () => null,
                        TableRow: TableRow,
                        TableBody: TableBody,
                    }}
                    itemContent={(idx, row) => (
                        <>
                            <TableCell
                                sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                {row.name}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }}>
                                <Link
                                    href={getLicenseUrl(row.license)}
                                    target='_blank'
                                    rel='noopener'
                                    color='text.secondary'>
                                    {row.license}
                                </Link>
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                <Link
                                    href={row.repository}
                                    target='_blank'
                                    rel='noopener'
                                    color='text.secondary'
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}>
                                    {row.repository}
                                </Link>
                            </TableCell>
                        </>
                    )}
                />
            )}
        </Box>
    );
}

function OpenSourceDialog({ open, onClose }) {
    const opensource = storage.transience.getValue('opensource') || {};
    const { infra, npm } = opensource;
    const [scrollParent, setScrollParent] = useState(null);

    const loadCache = (key) => {
        fetch(`/texts/${key}-licenses.csv`)
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch');
                return res.text();
            })
            .then((text) => {
                const parsed = parseCSV(text);
                storage.transience.setState(`opensource.${key}`, parsed);
            })
            .catch((err) => {
                console.error(`Failed to load ${key} licenses:`, err);
            });
    };

    useEffect(() => {
        if (!open) return;
        if (!infra || infra.length === 0) {
            loadCache('infra');
        }
        if (!npm || npm.length === 0) {
            loadCache('npm');
        }
    }, [open, infra, npm]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth fullScreen>
            <DialogTitle
                sx={{
                    color: 'primary.main',
                    textAlign: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                <Grid container spacing={0} direction='column' justifyContent='center' alignItems='center'>
                    <Typography variant='m_titleL' color='primary.main' sx={{ m: 2 }}>
                        Open Source Disclosure
                    </Typography>
                    <Typography align='center' variant='m_overline' color='primary.main'>
                        version 1
                    </Typography>
                </Grid>
                <IconButton onClick={onClose} size='small' sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflow: 'hidden', height: '90vh' }}>
                <ContainerScroll ref={setScrollParent} sx={{ px: 2 }}>
                    <LicenseTable title='Infrastructure & Middleware' data={infra} scrollParent={scrollParent} />
                    <LicenseTable title='NPM Packages & Libraries' data={npm} scrollParent={scrollParent} isLast />
                </ContainerScroll>
            </DialogContent>
        </Dialog>
    );
}

export { OpenSourceDialog };
